import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import AdminLayout from '../components/AdminLayout';
import { ImageInput } from '../components/ImagePicker';
import { useCompanyInfo } from '../../hooks/useAPI';
import { companyAPI } from '../../services/api';
import { 
  BuildingOffice2Icon, 
  EnvelopeIcon, 
  MapPinIcon,
  LinkIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const CompanyInfoPage = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const [faviconUrl, setFaviconUrl] = useState('');
  const { data: companyInfo, isLoading, error } = useCompanyInfo();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty }
  } = useForm();

  // Update company info mutation
  const updateCompanyMutation = useMutation(
    (data) => companyAPI.updateInfo(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('companyInfo');
        toast.success('Company information updated successfully!');
        setIsEditing(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update company information');
      }
    }
  );

  // Reset form when company info loads
  useEffect(() => {
    if (companyInfo) {
      const formData = {
        companyName: companyInfo.companyName || '',
        tagline: companyInfo.tagline || '',
        description: companyInfo.description || '',
        aboutTitle: companyInfo.aboutTitle || '',
        mission: companyInfo.mission || '',
        vision: companyInfo.vision || '',
        email: companyInfo.email || '',
        phone: companyInfo.phone || '',
        address: companyInfo.address || '',
        logo: companyInfo.logo || '',
        favicon: companyInfo.favicon || '',
      };

      // Set image state
      setLogoUrl(companyInfo.logo || '');
      setFaviconUrl(companyInfo.favicon || '');

      // Handle aboutDescription parsing
      if (companyInfo.aboutDescription) {
        try {
          const aboutArray = typeof companyInfo.aboutDescription === 'string' 
            ? JSON.parse(companyInfo.aboutDescription)
            : companyInfo.aboutDescription;
          formData.aboutDescription = Array.isArray(aboutArray) 
            ? aboutArray.join('\n\n') 
            : aboutArray;
        } catch (e) {
          formData.aboutDescription = companyInfo.aboutDescription;
        }
      }

      // Handle social media parsing
      if (companyInfo.socialMedia) {
        try {
          const socialData = typeof companyInfo.socialMedia === 'string' 
            ? JSON.parse(companyInfo.socialMedia)
            : companyInfo.socialMedia;
          formData.linkedin = socialData.linkedin || '';
          formData.twitter = socialData.twitter || '';
          formData.facebook = socialData.facebook || '';
          formData.instagram = socialData.instagram || '';
        } catch (e) {
          // Handle error silently
        }
      }

      reset(formData);
    }
  }, [companyInfo, reset]);

  const onSubmit = async (data) => {
    // Process aboutDescription as array
    const aboutDescriptionArray = data.aboutDescription
      .split('\n\n')
      .filter(paragraph => paragraph.trim())
      .map(paragraph => paragraph.trim());

    // Process social media as JSON
    const socialMedia = {
      linkedin: data.linkedin || '',
      twitter: data.twitter || '',
      facebook: data.facebook || '',
      instagram: data.instagram || '',
    };

    const submitData = {
      ...data,
      logo: logoUrl,
      favicon: faviconUrl,
      aboutDescription: JSON.stringify(aboutDescriptionArray),
      socialMedia,
    };

    // Remove individual social media fields
    delete submitData.linkedin;
    delete submitData.twitter;
    delete submitData.facebook;
    delete submitData.instagram;

    updateCompanyMutation.mutate(submitData);
  };

  const handleCancel = () => {
    reset();
    setIsEditing(false);
  };

  if (isLoading) {
    return (
      <AdminLayout title="Company Information">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="Company Information">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading company information</h3>
              <div className="mt-2 text-sm text-red-700">
                {error.message}
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Company Information">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <BuildingOffice2Icon className="h-6 w-6 text-gray-400 mr-3" />
                <h2 className="text-lg font-medium text-gray-900">Company Information</h2>
              </div>
              
              <div className="flex space-x-3">
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Edit Information
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleCancel}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <XMarkIcon className="h-4 w-4 mr-2" />
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmit(onSubmit)}
                      disabled={updateCompanyMutation.isLoading || !isDirty}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <CheckIcon className="h-4 w-4 mr-2" />
                      {updateCompanyMutation.isLoading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit(onSubmit)} className="p-6">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              
              {/* Basic Information */}
              <div className="lg:col-span-2">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                {isEditing && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-sm text-blue-800">
                      <strong>💡 Media Upload:</strong> Use the "Browse" button to select images from your Media Library, 
                      or paste external URLs directly. Visit the 
                      <strong className="mx-1">Media Library</strong> section to upload and manage your images.
                    </p>
                  </div>
                )}
              </div>

              {/* Company Name */}
              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    id="companyName"
                    {...register('companyName', { required: 'Company name is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="py-2 text-gray-900">{companyInfo?.companyName || 'Not set'}</p>
                )}
                {errors.companyName && (
                  <p className="mt-1 text-sm text-red-600">{errors.companyName.message}</p>
                )}
              </div>

              {/* Tagline */}
              <div>
                <label htmlFor="tagline" className="block text-sm font-medium text-gray-700 mb-2">
                  Tagline
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    id="tagline"
                    {...register('tagline')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="py-2 text-gray-900">{companyInfo?.tagline || 'Not set'}</p>
                )}
              </div>

              {/* Logo */}
              <div>
                {isEditing ? (
                  <ImageInput
                    label="Logo"
                    value={logoUrl}
                    onChange={setLogoUrl}
                    placeholder="Select or enter logo URL"
                    category="logos"
                  />
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
                    <div className="py-2">
                      {companyInfo?.logo ? (
                        <div className="flex items-center space-x-3">
                          <img 
                            src={companyInfo.logo} 
                            alt="Logo" 
                            className="h-12 w-auto max-w-32 object-contain border border-gray-200 rounded p-1 bg-white" 
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                          <span className="text-red-500 text-sm hidden">Invalid image URL</span>
                          <div className="flex-1">
                            <p className="text-gray-600 text-sm truncate">{companyInfo.logo}</p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-500 italic">No logo set</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Favicon */}
              <div>
                {isEditing ? (
                  <ImageInput
                    label="Favicon"
                    value={faviconUrl}
                    onChange={setFaviconUrl}
                    placeholder="Select or enter favicon URL"
                    category="favicons"
                  />
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Favicon</label>
                    <div className="py-2">
                      {companyInfo?.favicon ? (
                        <div className="flex items-center space-x-3">
                          <img 
                            src={companyInfo.favicon} 
                            alt="Favicon" 
                            className="h-6 w-6 object-contain border border-gray-200 rounded bg-white" 
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                          <span className="text-red-500 text-sm hidden">Invalid image URL</span>
                          <div className="flex-1">
                            <p className="text-gray-600 text-sm truncate">{companyInfo.favicon}</p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-500 italic">No favicon set</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="lg:col-span-2">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                {isEditing ? (
                  <textarea
                    id="description"
                    rows={3}
                    {...register('description')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="py-2 text-gray-900">{companyInfo?.description || 'Not set'}</p>
                )}
              </div>

              {/* About Section */}
              <div className="lg:col-span-2 border-t border-gray-200 pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">About Section</h3>
              </div>

              {/* About Title */}
              <div>
                <label htmlFor="aboutTitle" className="block text-sm font-medium text-gray-700 mb-2">
                  About Title
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    id="aboutTitle"
                    {...register('aboutTitle')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="py-2 text-gray-900">{companyInfo?.aboutTitle || 'Not set'}</p>
                )}
              </div>

              {/* About Description */}
              <div className="lg:col-span-2">
                <label htmlFor="aboutDescription" className="block text-sm font-medium text-gray-700 mb-2">
                  About Description
                  {isEditing && <span className="text-sm text-gray-500 ml-2">(Separate paragraphs with double line breaks)</span>}
                </label>
                {isEditing ? (
                  <textarea
                    id="aboutDescription"
                    rows={6}
                    {...register('aboutDescription')}
                    placeholder="Enter about description paragraphs separated by double line breaks..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <div className="py-2 space-y-2">
                    {companyInfo?.aboutDescription ? (
                      (() => {
                        try {
                          const descriptions = typeof companyInfo.aboutDescription === 'string' 
                            ? JSON.parse(companyInfo.aboutDescription)
                            : companyInfo.aboutDescription;
                          return Array.isArray(descriptions) 
                            ? descriptions.map((desc, index) => (
                                <p key={index} className="text-gray-900">{desc}</p>
                              ))
                            : <p className="text-gray-900">{descriptions}</p>;
                        } catch (e) {
                          return <p className="text-gray-900">{companyInfo.aboutDescription}</p>;
                        }
                      })()
                    ) : (
                      <p className="text-gray-500">Not set</p>
                    )}
                  </div>
                )}
              </div>

              {/* Mission & Vision */}
              <div className="lg:col-span-2 border-t border-gray-200 pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Mission & Vision</h3>
              </div>

              {/* Mission */}
              <div>
                <label htmlFor="mission" className="block text-sm font-medium text-gray-700 mb-2">
                  Mission
                </label>
                {isEditing ? (
                  <textarea
                    id="mission"
                    rows={3}
                    {...register('mission')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="py-2 text-gray-900">{companyInfo?.mission || 'Not set'}</p>
                )}
              </div>

              {/* Vision */}
              <div>
                <label htmlFor="vision" className="block text-sm font-medium text-gray-700 mb-2">
                  Vision
                </label>
                {isEditing ? (
                  <textarea
                    id="vision"
                    rows={3}
                    {...register('vision')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="py-2 text-gray-900">{companyInfo?.vision || 'Not set'}</p>
                )}
              </div>

              {/* Contact Information */}
              <div className="lg:col-span-2 border-t border-gray-200 pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <EnvelopeIcon className="h-5 w-5 mr-2 text-gray-400" />
                  Contact Information
                </h3>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                {isEditing ? (
                  <input
                    type="email"
                    id="email"
                    {...register('email', { 
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="py-2 text-gray-900">{companyInfo?.email || 'Not set'}</p>
                )}
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                {isEditing ? (
                  <input
                    type="tel"
                    id="phone"
                    {...register('phone')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="py-2 text-gray-900">{companyInfo?.phone || 'Not set'}</p>
                )}
              </div>

              {/* Address */}
              <div className="lg:col-span-2">
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPinIcon className="h-4 w-4 inline mr-1" />
                  Address
                </label>
                {isEditing ? (
                  <textarea
                    id="address"
                    rows={2}
                    {...register('address')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="py-2 text-gray-900">{companyInfo?.address || 'Not set'}</p>
                )}
              </div>

              {/* Social Media */}
              <div className="lg:col-span-2 border-t border-gray-200 pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <LinkIcon className="h-5 w-5 mr-2 text-gray-400" />
                  Social Media
                </h3>
              </div>

              {/* LinkedIn */}
              <div>
                <label htmlFor="linkedin" className="block text-sm font-medium text-gray-700 mb-2">
                  LinkedIn
                </label>
                {isEditing ? (
                  <input
                    type="url"
                    id="linkedin"
                    {...register('linkedin')}
                    placeholder="https://linkedin.com/company/yourcompany"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="py-2 text-gray-900">
                    {(() => {
                      try {
                        const social = typeof companyInfo?.socialMedia === 'string' 
                          ? JSON.parse(companyInfo.socialMedia)
                          : companyInfo?.socialMedia;
                        return social?.linkedin || 'Not set';
                      } catch (e) {
                        return 'Not set';
                      }
                    })()}
                  </p>
                )}
              </div>

              {/* Twitter */}
              <div>
                <label htmlFor="twitter" className="block text-sm font-medium text-gray-700 mb-2">
                  Twitter
                </label>
                {isEditing ? (
                  <input
                    type="url"
                    id="twitter"
                    {...register('twitter')}
                    placeholder="https://twitter.com/yourcompany"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="py-2 text-gray-900">
                    {(() => {
                      try {
                        const social = typeof companyInfo?.socialMedia === 'string' 
                          ? JSON.parse(companyInfo.socialMedia)
                          : companyInfo?.socialMedia;
                        return social?.twitter || 'Not set';
                      } catch (e) {
                        return 'Not set';
                      }
                    })()}
                  </p>
                )}
              </div>

              {/* Facebook */}
              <div>
                <label htmlFor="facebook" className="block text-sm font-medium text-gray-700 mb-2">
                  Facebook
                </label>
                {isEditing ? (
                  <input
                    type="url"
                    id="facebook"
                    {...register('facebook')}
                    placeholder="https://facebook.com/yourcompany"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="py-2 text-gray-900">
                    {(() => {
                      try {
                        const social = typeof companyInfo?.socialMedia === 'string' 
                          ? JSON.parse(companyInfo.socialMedia)
                          : companyInfo?.socialMedia;
                        return social?.facebook || 'Not set';
                      } catch (e) {
                        return 'Not set';
                      }
                    })()}
                  </p>
                )}
              </div>

              {/* Instagram */}
              <div>
                <label htmlFor="instagram" className="block text-sm font-medium text-gray-700 mb-2">
                  Instagram
                </label>
                {isEditing ? (
                  <input
                    type="url"
                    id="instagram"
                    {...register('instagram')}
                    placeholder="https://instagram.com/yourcompany"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="py-2 text-gray-900">
                    {(() => {
                      try {
                        const social = typeof companyInfo?.socialMedia === 'string' 
                          ? JSON.parse(companyInfo.socialMedia)
                          : companyInfo?.socialMedia;
                        return social?.instagram || 'Not set';
                      } catch (e) {
                        return 'Not set';
                      }
                    })()}
                  </p>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

export default CompanyInfoPage;